import React, { useState, useEffect, useMemo } from "react";
import TrendGraphModal from './TrendGraphModal';
import ComparisonGraphModal from './ComparisonGraphModal';
import TrendableMetric from '../../jsx/TrendableMetric';
import {computeYtdValue, useTrendModalHelper} from '../../utils/useTrendModalHelper';
import { fetchHistoricPayrolls } from "../../services/fetchHistoricPayrolls";
import useModalStore from "../../stores/modalStore";
import { hasPermission } from "../../utils/permissions";
import {BACKEND_URL} from "../../utils/constants";
import {toFloat} from "../../utils/formatting";
import getFirebaseAuthToken from '../../services/fireBaseAuthenticationService';
import { GitCompareArrows } from 'lucide-react';


const EarningsSummaryModal = ({
                                  historicPayrolls,
                                  setHistoricPayrolls,
                              }) => {
    const show = useModalStore(state => state.showEarningsModal);
    const closeModal = useModalStore(state => state.closeModal);

    const [payrollIndex, setPayrollIndex] = useState(0);
    const [hasInitializedIndex, setHasInitializedIndex] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState(new Set());
    const [comparisonSeries, setComparisonSeries] = useState([]);
    const openModal = useModalStore(state => state.openModal);

    const {
        trendData,
        trendTitle,
        xKey,
        yKey,
        openTrendModal
    } = useTrendModalHelper();

    const memoizedTrendGraphModal = useMemo(() => (
        <TrendGraphModal
            title={trendTitle}
            data={trendData}
            xKey={xKey}
            yKey={yKey}
        />
    ), [trendData, trendTitle, xKey, yKey]);

    useEffect(() => {
        if (show && historicPayrolls.length === 0) {
            const load = async () => {
                try {
                    const runs = await fetchHistoricPayrolls(getFirebaseAuthToken, BACKEND_URL);
                    setHistoricPayrolls(runs || []);
                } catch (err) {
                    console.error("Failed to fetch payroll runs:", err);
                    setHistoricPayrolls([]);
                }
            };

            load();
        }
    }, [show, historicPayrolls.length, setHistoricPayrolls]);

    useEffect(() => {
        if (show && !hasInitializedIndex && historicPayrolls.length > 0) {
            setPayrollIndex(historicPayrolls.length - 1);
            setHasInitializedIndex(true);
        }
    }, [show, historicPayrolls.length, hasInitializedIndex]);

    useEffect(() => {
        if (!show) {
            setHasInitializedIndex(false);
            setSelectedMetrics(new Set());
        }
    }, [show]);

    if (!show) return null;
    if (!hasPermission('view_earnings')) return null;

    const currentPayroll = historicPayrolls[payrollIndex];

    const totalServices = currentPayroll?.employee_data?.reduce((sum, emp) => sum + (emp.services_in_period || 0), 0) || 0;
    const totalNormalHoursWorked = currentPayroll?.employee_data?.reduce((sum, emp) => sum + (emp.normal_hours || 0), 0) || 0;
    const totalOvertimeHoursWorked = currentPayroll?.employee_data?.reduce((sum, emp) => sum + (emp.overtime_hours || 0), 0) || 0;
    const totalHoursWorked = totalNormalHoursWorked + totalOvertimeHoursWorked;
    const totalDaysWorked = currentPayroll?.employee_data?.reduce((sum, emp) => sum + (emp.days_worked || 0), 0) || 0;
    const serviceRate = totalServices / (totalHoursWorked || 1);

    // Metric definitions for comparison feature
    const metricDefs = [
        {
            label: "Total Employees Who Worked",
            isDollars: false,
            xKey: "Period",
            yKey: "Employees",
            accessorFn: (p) => p.employee_data.filter((e) => (e.normal_hours || 0) > 0 || (e.overtime_hours || 0) > 0).length,
        },
        {
            label: "Total Days Worked",
            isDollars: false,
            xKey: "Period",
            yKey: "Days",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (e.days_worked || 0), 0),
        },
        {
            label: "Period Total Services",
            isDollars: true,
            xKey: "Period",
            yKey: "Services",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (e.services_in_period || 0), 0),
        },
        {
            label: "Bonuses Paid This Period",
            isDollars: true,
            xKey: "Period",
            yKey: "Bonuses Paid",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (toFloat(e.income_in_period?.bonus) || 0), 0),
        },
        {
            label: "Total Normal Hours Worked",
            isDollars: false,
            xKey: "Period",
            yKey: "Normal Hours",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (e.normal_hours || 0), 0),
        },
        {
            label: "Total Overtime Hours Worked",
            isDollars: false,
            xKey: "Period",
            yKey: "Overtime Hours",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (e.overtime_hours || 0), 0),
        },
        {
            label: "Total Hours Worked",
            isDollars: false,
            xKey: "Period",
            yKey: "Total Hours",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (e.normal_hours || 0) + (e.overtime_hours || 0), 0),
        },
        {
            label: "Total Scheduled Hours",
            isDollars: false,
            xKey: "Period",
            yKey: "Scheduled Hours",
            accessorFn: (p) => p.employee_data.reduce((sum, e) => sum + (e.scheduled_hours || 0), 0),
        },
        {
            label: "Percent of Scheduled Hours Worked",
            isDollars: false,
            xKey: "Period",
            yKey: "% Scheduled",
            accessorFn: (p) => {
                const scheduled = p.employee_data.reduce((sum, e) => sum + (toFloat(e.scheduled_hours) || 0), 0);
                const worked = p.employee_data.reduce((sum, e) => sum + (toFloat(e.normal_hours) || 0) + (toFloat(e.overtime_hours) || 0), 0);
                return scheduled > 0 ? (worked / scheduled) * 100 : 0;
            },
        },
        {
            label: "Service Rate",
            isDollars: true,
            xKey: "Period",
            yKey: "Service Rate",
            accessorFn: (p) => {
                const totalHrs = p.employee_data.reduce((sum, e) => sum + (e.normal_hours || 0) + (e.overtime_hours || 0), 0);
                const totalSrv = p.employee_data.reduce((sum, e) => sum + (e.services_in_period || 0), 0);
                return totalSrv / (totalHrs || 1);
            },
        },
    ];

    const toggleMetric = (label) => {
        setSelectedMetrics(prev => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
    };

    const handleCompare = () => {
        const series = metricDefs
            .filter(m => selectedMetrics.has(m.label))
            .map(m => ({
                label: m.label,
                isDollars: m.isDollars,
                xKey: m.xKey,
                yKey: m.yKey,
                data: historicPayrolls.map(p => ({
                    [m.xKey]: p.period_end,
                    [m.yKey]: m.accessorFn(p),
                })),
            }));
        setComparisonSeries(series);
        openModal("comparisonGraphModal");
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh] w-full max-w-4xl overflow-hidden">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Earnings Summary</h2>
                        {selectedMetrics.size >= 2 && (
                            <button
                                onClick={handleCompare}
                                className="!mt-1 !px-3 !py-1 !text-xs !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-lg !transition-colors !inline-flex !items-center !gap-1.5"
                            >
                                <GitCompareArrows size={12} />
                                Compare Selected ({selectedMetrics.size})
                            </button>
                        )}
                        {selectedMetrics.size === 1 && (
                            <p className="mt-1 text-xs text-gray-400">Select one more metric to compare</p>
                        )}
                    </div>
                    <button
                        onClick={() => closeModal("earningsModal")}
                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                    >
                        ✕
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto p-4 flex-1 text-center">
                    {currentPayroll ? (
                        <>
                            <p>
                                <strong>Period:</strong> {currentPayroll.period_start} to {currentPayroll.period_end}
                            </p>

                            {/* All TrendableMetric components go here */}
                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Total Employees Who Worked")} onChange={() => toggleMetric("Total Employees Who Worked")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Total Employees Who Worked"
                                currentValue={currentPayroll.employee_data.filter(
                                    (e) => (e.normal_hours || 0) > 0 || (e.overtime_hours || 0) > 0
                                ).length}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="Employees"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.filter(
                                        (e) => (e.normal_hours || 0) > 0 || (e.overtime_hours || 0) > 0
                                    ).length}
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Total Days Worked")} onChange={() => toggleMetric("Total Days Worked")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Total Days Worked"
                                currentValue={totalDaysWorked}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="Days"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce((sum, e) => sum + (e.days_worked || 0), 0)
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Period Total Services")} onChange={() => toggleMetric("Period Total Services")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Period Total Services"
                                currentValue={totalServices}
                                isDollars={true}
                                graphLabelX="Period"
                                graphLabelY="Services"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce((sum, e) => sum + (e.services_in_period || 0), 0)
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <TrendableMetric
                                label="YTD Total Services"
                                currentValue={computeYtdValue(
                                    historicPayrolls,
                                    null,
                                    "services_in_period",
                                    currentPayroll.period_end
                                )}
                                isDollars={true}
                                graphLabelX="Period"
                                graphLabelY="YTD Services"
                                openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentPayroll.period_end).getFullYear() })}
                                accessorFn={(p) =>
                                    p.employee_data.reduce((sum, e) => sum + (e.services_in_period || 0), 0)
                                }
                                data={historicPayrolls}
                            />

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Bonuses Paid This Period")} onChange={() => toggleMetric("Bonuses Paid This Period")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Bonuses Paid This Period"
                                currentValue={
                                    currentPayroll.employee_data.reduce(
                                        (sum, e) => sum + (toFloat(e.income_in_period?.bonus) || 0),
                                        0
                                    )
                                }
                                isDollars={true}
                                graphLabelX="Period"
                                graphLabelY="Bonuses Paid"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce(
                                        (sum, e) => sum + (toFloat(e.income_in_period?.bonus) || 0),
                                        0
                                    )
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <TrendableMetric
                                label="YTD Bonuses Paid"
                                currentValue={computeYtdValue(
                                    historicPayrolls,
                                    null,
                                    "income_in_period.bonus",
                                    currentPayroll.period_end
                                )}
                                isDollars={true}
                                graphLabelX="Period"
                                graphLabelY="YTD Bonus total"
                                openModalFn={(...args) => openTrendModal(...args, { ytd: true, year: new Date(currentPayroll.period_end).getFullYear() })}
                                accessorFn={(p) =>
                                    p.employee_data.reduce((sum, e) => sum + toFloat(e.income_in_period?.bonus || 0), 0)
                                }
                                data={historicPayrolls}
                            />

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Total Normal Hours Worked")} onChange={() => toggleMetric("Total Normal Hours Worked")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Total Normal Hours Worked"
                                currentValue={totalNormalHoursWorked}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="Normal Hours"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce((sum, e) => sum + (e.normal_hours || 0), 0)
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Total Overtime Hours Worked")} onChange={() => toggleMetric("Total Overtime Hours Worked")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Total Overtime Hours Worked"
                                currentValue={totalOvertimeHoursWorked}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="Overtime Hours"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce((sum, e) => sum + (e.overtime_hours || 0), 0)
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Total Hours Worked")} onChange={() => toggleMetric("Total Hours Worked")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Total Hours Worked"
                                currentValue={totalHoursWorked}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="Total Hours"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce(
                                        (sum, e) => sum + (e.normal_hours || 0) + (e.overtime_hours || 0),
                                        0
                                    )
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Total Scheduled Hours")} onChange={() => toggleMetric("Total Scheduled Hours")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Total Scheduled Hours"
                                currentValue={currentPayroll?.employee_data?.reduce((sum, emp) => sum + (emp.scheduled_hours || 0), 0) || 0}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="Total Hours"
                                openModalFn={openTrendModal}
                                accessorFn={(p) =>
                                    p.employee_data.reduce(
                                        (sum, e) => sum + (e.scheduled_hours || 0),
                                        0
                                    )
                                }
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Percent of Scheduled Hours Worked")} onChange={() => toggleMetric("Percent of Scheduled Hours Worked")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Percent of Scheduled Hours Worked"
                                currentValue={(() => {
                                    const scheduled = currentPayroll.employee_data.reduce(
                                        (sum, e) => sum + (toFloat(e.scheduled_hours) || 0),
                                        0
                                    );
                                    const worked = currentPayroll.employee_data.reduce(
                                        (sum, e) => sum + (toFloat(e.normal_hours) || 0) + (toFloat(e.overtime_hours) || 0),
                                        0
                                    );
                                    return scheduled > 0 ? (worked / scheduled) * 100 : 0;
                                })()}
                                isDollars={false}
                                graphLabelX="Period"
                                graphLabelY="% Scheduled Hours Worked"
                                openModalFn={openTrendModal}
                                accessorFn={(p) => {
                                    const scheduled = p.employee_data.reduce(
                                        (sum, e) => sum + (toFloat(e.scheduled_hours) || 0),
                                        0
                                    );
                                    const worked = p.employee_data.reduce(
                                        (sum, e) => sum + (toFloat(e.normal_hours) || 0) + (toFloat(e.overtime_hours) || 0),
                                        0
                                    );
                                    return scheduled > 0 ? (worked / scheduled) * 100 : 0;
                                }}
                                data={historicPayrolls}
                            />
                            </div>

                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedMetrics.has("Service Rate")} onChange={() => toggleMetric("Service Rate")} className="accent-indigo-600" />
                            <TrendableMetric
                                label="Service Rate"
                                currentValue={serviceRate}
                                isDollars={true}
                                graphLabelX="Period"
                                graphLabelY="Service Rate"
                                openModalFn={openTrendModal}
                                accessorFn={(p) => {
                                    const totalHrs = p.employee_data.reduce(
                                        (sum, e) => sum + (e.normal_hours || 0) + (e.overtime_hours || 0), 0
                                    );
                                    const totalSrv = p.employee_data.reduce(
                                        (sum, e) => sum + (e.services_in_period || 0), 0
                                    );
                                    return totalSrv / (totalHrs || 1);
                                }}
                                data={historicPayrolls}
                            />
                            </div>
                        </>
                    ) : (
                        <p>Loading payroll summary...</p>
                    )}
                </div>

                {/* Footer (Sticky) */}
                <div className="border-t border-gray-100 px-6 py-3 bg-white sticky bottom-0">
                    <div className="flex justify-center items-center gap-3">
                        <button
                            onClick={() => setPayrollIndex(i => Math.max(0, i - 1))}
                            disabled={payrollIndex === 0}
                            className="!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 disabled:!opacity-30 !transition-colors"
                        >
                            ← Previous
                        </button>
                        <span className="text-xs text-gray-400 font-medium">
                            {payrollIndex + 1} / {historicPayrolls.length}
                        </span>
                        <button
                            onClick={() => setPayrollIndex(i => Math.min(historicPayrolls.length - 1, i + 1))}
                            disabled={payrollIndex >= historicPayrolls.length - 1}
                            className="!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 disabled:!opacity-30 !transition-colors"
                        >
                            Next →
                        </button>
                    </div>
                </div>

                {memoizedTrendGraphModal}
                <ComparisonGraphModal series={comparisonSeries} />
            </div>
        </div>

    );
};

export default EarningsSummaryModal;
